import React from 'react';
import { type MessageDescriptor, defineMessages, useIntl } from 'react-intl-next';

import { Box, Stack } from '@atlaskit/primitives/compiled';

import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useCollabNetworkStatus } from '@confluence/collab-network-status';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type { PresenceContentMode, PresenceContentType } from '../presenceTypes';

import { NoCollaboratorsDialogShareOptions } from './NoCollaboratorsDialogShareOptions';

const i18n = defineMessages({
	blogpostViewing: {
		id: 'team-presence.menu.empty-state.blogpost.viewing',
		defaultMessage: 'You’re the only one viewing this blog post.',
		description:
			'Text shown when the user is the only one viewing the blog post and has no other collaborators.',
	},
	blogpostEditing: {
		id: 'team-presence.menu.empty-state.blogpost.editing',
		defaultMessage: 'You’re the only one editing this blog post.',
		description:
			'Text shown when the user is the only one editing the blog post and has no other collaborators.',
	},
	pageViewing: {
		id: 'team-presence.menu.empty-state.page.viewing',
		defaultMessage: 'You’re the only one viewing this page.',
		description:
			'Text shown when the user is the only one viewing the page and has no other collaborators.',
	},
	pageEditing: {
		id: 'team-presence.menu.empty-state.page.editing',
		defaultMessage: 'You’re the only one editing this page.',
		description:
			'Text shown when the user is the only one editing the page and has no other collaborators.',
	},
	shareItWithOthersText: {
		id: 'team-presence.menu.empty-state.share-it-with-others',
		defaultMessage: 'Share it with others who might be interested.',
		description: 'Text encouraging the user to share the page with others.',
	},
	publishToShareItWithOthersText: {
		id: 'team-presence.menu.empty-state.publish-to-share-it-with-others',
		defaultMessage: 'Publish it to share with others who might be interested.',
		description: 'Text encouraging the user to share the page with others by publishing.',
	},
	copyLinkButtonText: {
		id: 'team-presence.menu.empty-state.copy-button',
		defaultMessage: 'Copy link',
		description: 'Call to action button to copy the link to the content.',
	},
	sharePageButtonText: {
		id: 'team-presence.menu.empty-state.page.share-button',
		defaultMessage: 'Share page',
		description: 'Call to action button to share the page with others.',
	},
	shareBlogPostButtonText: {
		id: 'team-presence.menu.empty-state.blogpost.share-button',
		defaultMessage: 'Share blog post',
		description: 'Call to action button to share the blog post with others.',
	},
	sharePageButtonClickedText: {
		id: 'team-presence.menu.empty-state.page.share-button.clicked',
		defaultMessage: 'Page shared',
		description: 'Button text to indicate that the page has been shared.',
	},
	shareBlogPostButtonClickedText: {
		id: 'team-presence.menu.empty-state.blogpost.share-button.clicked',
		defaultMessage: 'Blog post shared',
		description: 'Button text to indicate that the blog post has been shared.',
	},
	whiteboardModalDescription: {
		id: 'team-presence.menu.empty-state.whiteboard',
		defaultMessage: 'You’re the only one on this whiteboard.',
		description:
			'Text shown when the user is the only one viewing the whiteboard and has no other collaborators.',
	},
	shareWhiteboardButtonText: {
		id: 'team-presence.menu.empty-state.whiteboard.share-button',
		defaultMessage: 'Share whiteboard',
		description: 'Call to action button to share the whiteboard with others.',
	},
	shareWhiteboardButtonClickedText: {
		id: 'team-presence.menu.empty-state.whiteboard.share-button.clicked',
		defaultMessage: 'Whiteboard shared',
		description: 'Button text to indicate that the whiteboard has been shared.',
	},
	databaseModalDescription: {
		id: 'team-presence.menu.empty-state.database',
		defaultMessage: 'You’re the only one on this database.',
		description:
			'Text shown when the user is the only one viewing the database and has no other collaborators.',
	},
	shareDatabaseButtonText: {
		id: 'team-presence.menu.empty-state.database.share-button',
		defaultMessage: 'Share database',
		description: 'Call to action button to share the database with others.',
	},
	shareDatabaseButtonClickedText: {
		id: 'team-presence.menu.empty-state.database.share-button.clicked',
		defaultMessage: 'Database shared',
		description: 'Button text to indicate that the database has been shared.',
	},
	liveDocViewingModalDescription: {
		id: 'team-presence.menu.empty-state.live-doc.viewing',
		defaultMessage: 'You’re the only one viewing this live doc.',
		description:
			'Text shown when the user is the only one viewing the live doc and has no other collaborators.',
	},
	liveDocEditingModalDescription: {
		id: 'team-presence.menu.empty-state.live-doc.editing',
		defaultMessage: 'You’re the only one editing this live doc.',
		description:
			'Text shown when the user is the only one editing the live doc and has no other collaborators.',
	},
	shareLiveDocButtonText: {
		id: 'team-presence.menu.empty-state.live-doc.share-button',
		defaultMessage: 'Share live doc',
		description: 'Call to action button to share the live doc with others.',
	},
	shareLiveDocButtonClickedText: {
		id: 'team-presence.menu.empty-state.live-doc.share-button.clicked',
		defaultMessage: 'Live doc shared',
		description: 'Button text to indicate that the live doc has been shared.',
	},
});

type ShareDialogTextType = Record<
	string,
	{
		modalDescriptionLineOne: string;
		modalDescriptionLineTwo: string;
		shareButtonText: string;
		shareButtonClickedText: string;
	}
>;

export type NoCollaboratorsDialogProps = {
	onShareClick: (analyticsSource: string) => void;
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	spaceKey: string;
	testId?: string;
};

const contentTypeToTranslationMap = (
	formatMessage: (descriptor: MessageDescriptor) => string,
	contentMode: PresenceContentMode,
	isShareButtonDisabled: boolean,
): ShareDialogTextType => {
	return {
		blogpost: {
			modalDescriptionLineOne: formatMessage(
				contentMode === 'edit' ? i18n.blogpostEditing : i18n.blogpostViewing,
			),
			modalDescriptionLineTwo: formatMessage(
				isShareButtonDisabled ? i18n.publishToShareItWithOthersText : i18n.shareItWithOthersText,
			),
			shareButtonText: formatMessage(i18n.shareBlogPostButtonText),
			shareButtonClickedText: formatMessage(i18n.shareBlogPostButtonClickedText),
		},
		page: {
			modalDescriptionLineOne: formatMessage(
				contentMode === 'edit' ? i18n.pageEditing : i18n.pageViewing,
			),
			modalDescriptionLineTwo: formatMessage(
				isShareButtonDisabled ? i18n.publishToShareItWithOthersText : i18n.shareItWithOthersText,
			),
			shareButtonText: formatMessage(i18n.sharePageButtonText),
			shareButtonClickedText: formatMessage(i18n.sharePageButtonClickedText),
		},
		whiteboard: {
			modalDescriptionLineOne: formatMessage(i18n.whiteboardModalDescription),
			modalDescriptionLineTwo: formatMessage(i18n.shareItWithOthersText),
			shareButtonText: formatMessage(i18n.shareWhiteboardButtonText),
			shareButtonClickedText: formatMessage(i18n.shareWhiteboardButtonClickedText),
		},
		database: {
			modalDescriptionLineOne: formatMessage(i18n.databaseModalDescription),
			modalDescriptionLineTwo: formatMessage(i18n.shareItWithOthersText),
			shareButtonText: formatMessage(i18n.shareDatabaseButtonText),
			shareButtonClickedText: formatMessage(i18n.shareDatabaseButtonClickedText),
		},
		live: {
			modalDescriptionLineOne: formatMessage(
				contentMode === 'edit'
					? i18n.liveDocEditingModalDescription
					: i18n.liveDocViewingModalDescription,
			),
			modalDescriptionLineTwo: formatMessage(i18n.shareItWithOthersText),
			shareButtonText: formatMessage(i18n.shareLiveDocButtonText),
			shareButtonClickedText: formatMessage(i18n.shareLiveDocButtonClickedText),
		},
	};
};

export const NoCollaboratorsDialog = ({
	contentMode,
	onShareClick,
	contentId,
	contentType,
	spaceKey,
	testId,
}: NoCollaboratorsDialogProps) => {
	const { formatMessage } = useIntl();
	const [{ isCollabOffline }] = useCollabNetworkStatus();

	// Network status is used when:
	// - isCollabOffline: The collab provider has detected a network disconnection
	// - platform_editor_offline_editing_web: Parent experiment for offline editing experience (must be enabled)
	// - platform_editor_offline_editing_wrapper_override: Kill switch to disable this feature (must be disabled)
	const isNetworkDisabled =
		isCollabOffline &&
		expVal('platform_editor_offline_editing_web', 'isEnabled', false) &&
		!fg('platform_editor_offline_editing_wrapper_override');
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	// Determine if the user is in edit mode. Live pages supports both edit and view modes, so
	// we need to do an additional check to see if we're in edit mode on a live pages.
	const isInEditMode = contentMode === 'edit' || (contentMode === 'live' && isLiveEditMode);

	const pageMode: PresenceContentMode = isInEditMode ? 'edit' : 'view';

	/*
	On edit classic page, there is no Share button (only "Update" button).
	Since there is no Share button on edit classic page with a share dialog, clicking the Share button on this modal doesn't do anything,
	so we are disabling the Share button on edit mode.
	The share button is also disabled if the network is disabled.
	*/
	const translations = contentTypeToTranslationMap(formatMessage, pageMode, isNetworkDisabled)[
		contentType === 'page' && contentMode === 'live' ? 'live' : contentType
	];

	return (
		<Stack testId={testId}>
			<Box paddingInline="space.200" paddingBlockStart="space.200">
				{translations.modalDescriptionLineOne}
				&nbsp;
				{translations.modalDescriptionLineTwo}
			</Box>
			<NoCollaboratorsDialogShareOptions
				contentId={contentId}
				contentMode={contentMode}
				contentType={contentType}
				pageMode={pageMode}
				spaceKey={spaceKey}
				copyLinkButtonLabel={formatMessage(i18n.copyLinkButtonText)}
				shareButtonLabel={translations.shareButtonText}
				isShareButtonDisabled={isNetworkDisabled}
				onShareClick={onShareClick}
			/>
		</Stack>
	);
};
